import * as React from 'react';

import { useLiveRef } from '@gitbook/util-react-hooks';

import type { PaletteAnchorRef } from './types';

/**
 * Fallback rect returned before the anchor has ever been measured, so positioning has a
 * defined value instead of throwing when the palette renders with a not-yet-mounted anchor.
 */
const EMPTY_RECT: DOMRect = {
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    toJSON: () => ({}),
};

/**
 * Returns a stable trigger ref for positioning a palette that keeps it pinned to its anchor's
 * last position while the anchor is being removed from the DOM.
 *
 * Palettes are frequently anchored to ephemeral editor nodes — a temporary `/`, `@`, or `#`
 * mark, or an inline decoration — that are torn out of the document the moment the palette
 * closes. Feeding that anchor straight to Floating UI makes the palette jump to the top-left as
 * it animates closed, because positioning is recomputed against a disconnected element whose
 * `getBoundingClientRect()` reports zeros. This hook returns a virtual element that mirrors the
 * live anchor while it is connected and freezes at the last measured rect once it disconnects,
 * so the palette stays in place through its exit animation.
 */
export function usePersistentAnchorRef(
    anchorRef: PaletteAnchorRef | null
): React.RefObject<HTMLElement | null> {
    // Read the latest anchor on each measure so the virtual element keeps working when the palette
    // switches from one anchor to another without a fresh identity.
    const anchorLiveRef = useLiveRef(anchorRef);
    const lastRectRef = React.useRef<DOMRect | null>(null);

    // Build the virtual element once and keep its identity stable: Floating UI reads it on every
    // reposition, and a stable object avoids needless reference churn.
    const [frozenRef] = React.useState<React.RefObject<HTMLElement | null>>(() => {
        const getAnchorElement = () => anchorLiveRef.current?.current ?? null;

        const virtualElement = {
            getBoundingClientRect: () => {
                const element = getAnchorElement();
                if (element?.isConnected) {
                    lastRectRef.current = element.getBoundingClientRect();
                }
                return lastRectRef.current ?? EMPTY_RECT;
            },
            getClientRects: () => {
                const element = getAnchorElement();
                if (element?.isConnected) {
                    return element.getClientRects();
                }
                return lastRectRef.current ? [lastRectRef.current] : [];
            },
            // Expose the real element while connected so Floating UI resolves the correct scroll
            // parents; drop it once disconnected so it doesn't read from a detached node.
            get contextElement() {
                const element = getAnchorElement();
                return element?.isConnected ? element : undefined;
            },
        };

        return { current: virtualElement as unknown as HTMLElement };
    });

    return frozenRef;
}
